import {IStorefrontSettings} from '../../../models/Storefront';

/**
 * The handful of store-setup questions the create wizard asks while the AI is building.
 *
 * The generation takes minutes, and for most of them the operator is doing nothing but watching a
 * spinner. These are the four answers a brand-new store needs before anyone can buy from it, and none
 * of them collide with the fields the assistant writes (it only ever touches styling), so they can be
 * gathered during the wait and flushed the moment the edit lease is free.
 *
 * Pure on purpose: what counts as answered, what gets sent, and what is refused are the three rules
 * with consequences here, and all three can be read without mounting Fluent.
 */
export interface IWizardSetupAnswers {
    defaultMarkup?: number;
    defaultMarkupType?: string;
    allowRequestQuote?: boolean;
    defaultSalesRep?: string;
    orderEmail?: string;
}

/** Shallow shape check only. The API is the one that finds out whether the address accepts mail. */
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Whether a text answer was actually given, rather than left blank or wiped back to nothing. */
const filled = (value?: string): boolean => (value ?? '').trim().length > 0;

/**
 * The patch to send for these answers, carrying only what was actually answered.
 *
 * The storefront PATCH merges sparsely and skips nulls, so an unanswered question must be absent
 * rather than sent as undefined: sending the whole shape would be a claim about fields the operator
 * never looked at. An empty markup type is the dropdown's Inherit option, which means "say nothing",
 * so it is dropped the same way a blank text field is.
 *
 * Returns undefined when nothing at all was answered, which is the caller's signal to skip the call.
 */
export const setupPatchBody = (answers: IWizardSetupAnswers): Partial<IStorefrontSettings> | undefined => {
    const patch: Partial<IStorefrontSettings> = {};
    if (answers.defaultMarkup !== undefined) patch.defaultMarkup = answers.defaultMarkup;
    if (filled(answers.defaultMarkupType)) patch.defaultMarkupType = answers.defaultMarkupType;
    if (answers.allowRequestQuote !== undefined) patch.allowRequestQuote = answers.allowRequestQuote;
    if (filled(answers.defaultSalesRep)) patch.defaultSalesRep = answers.defaultSalesRep;
    if (filled(answers.orderEmail)) patch.orderEmail = (answers.orderEmail ?? '').trim();
    return Object.keys(patch).length === 0 ? undefined : patch;
};

/**
 * Why these answers cannot be saved yet, in the operator's terms, or undefined when they can.
 *
 * Only two things are genuinely wrong rather than merely unanswered: quote requests turned on with
 * nobody to receive them, which produces a button on the storefront that emails no one, and an order
 * address that is not an address.
 */
export const setupValidationError = (answers: IWizardSetupAnswers): string | undefined => {
    if (answers.allowRequestQuote === true && !filled(answers.defaultSalesRep)) {
        return 'Choose who receives quote requests';
    }
    if (filled(answers.orderEmail) && !EMAIL_SHAPE.test((answers.orderEmail ?? '').trim())) {
        return 'Enter a valid email for order confirmations';
    }
    return undefined;
};

/**
 * How many of the four questions have been answered, for the Save button and the tab's progress.
 *
 * The markup amount and its type are one question: they are one control split in two, and counting
 * them separately would report a half-answered question as two.
 */
export const answeredCount = (answers: IWizardSetupAnswers): number => {
    let count = 0;
    if (answers.defaultMarkup !== undefined || filled(answers.defaultMarkupType)) count += 1;
    if (answers.allowRequestQuote !== undefined) count += 1;
    if (filled(answers.defaultSalesRep)) count += 1;
    if (filled(answers.orderEmail)) count += 1;
    return count;
};
